import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowDown, ArrowUpRight, Menu, X } from "lucide-react";
import LandingPhonePreview from "../components/landing/LandingPhonePreview";
import LandingCareDemo from "../components/landing/LandingCareDemo";
import LandingGamepowerScroll from "../components/landing/LandingGamepowerScroll";
import LandingHeroStage from "../components/landing/LandingHeroStage";
import "../landing-page.css";

const navigation = [
  ["leistungen", "Leistungen"], ["betreuung", "Betreuung"],
  ["gamepower", "Gamepower"], ["app", "App"],
];

const appViews = {
  today: { label: "Heute", title: "Was jetzt zählt." },
  services: { label: "Services", title: "Was du brauchst." },
  requests: { label: "Anfragen", title: "Was wir für dich organisieren." },
};

const services = [
  ["Ernährung", ["Planen", "Bestellen", "Liefern"]],
  ["Supplements", ["Bestand", "Nachschub", "Einnahme"]],
  ["Recovery", ["Physio", "Termine", "Maßnahmen"]],
  ["Alltag", ["Reinigung", "Reparaturen", "Besorgungen"]],
  ["Reise & Mobilität", ["Flüge", "Hotels", "Transfers"]],
  ["Individuelles", ["Tischreservierung", "Konzerttickets", "Persönliche Anliegen"]],
];

function RequestButton() {
  const navigate = useNavigate();
  return <button className="pc-landing-button pc-landing-request-button" type="button" onClick={() => navigate("/anfrage")}>Performance Care anfragen <ArrowUpRight size={20} aria-hidden="true" /></button>;
}

function Brand() {
  return <Link to="/" className="pc-landing-brand" aria-label="Performance Care Startseite">PERFORMANCE<span>CARE<span className="pc-landing-brand__point">.</span></span></Link>;
}

export default function LandingPage() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [appView, setAppView] = useState("today");
  const menuButton = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
        menuButton.current?.focus();
      }
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [menuOpen]);

  const selectAppView = (event) => {
    const keys = Object.keys(appViews);
    let index = keys.indexOf(appView);
    if (event.key === "ArrowRight") index = (index + 1) % keys.length;
    else if (event.key === "ArrowLeft") index = (index + keys.length - 1) % keys.length;
    else if (event.key === "Home") index = 0;
    else if (event.key === "End") index = keys.length - 1;
    else return;
    event.preventDefault();
    setAppView(keys[index]);
    document.getElementById(`pc-landing-tab-${keys[index]}`)?.focus();
  };

  return (
    <div className="pc-landing" lang="de">
      <a className="pc-landing-skip" href="#pc-landing-main">Zum Inhalt</a>
      <header className="pc-landing-header">
        <div className="pc-landing-shell pc-landing-header__inner">
          <Brand />
          <nav className="pc-landing-nav" aria-label="Hauptnavigation">
            {navigation.map(([id, label]) => <a key={id} href={`#${id}`}>{label}</a>)}
          </nav>
          <div className="pc-landing-header__actions">
            <Link className="pc-landing-login" to="/login">Login <ArrowUpRight size={16} aria-hidden="true" /></Link>
            <button ref={menuButton} type="button" className="pc-landing-menu-toggle" aria-label={menuOpen ? "Menü schließen" : "Menü öffnen"} aria-expanded={menuOpen} aria-controls="pc-landing-mobile-nav" onClick={() => setMenuOpen(!menuOpen)}>
              {menuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
        <nav id="pc-landing-mobile-nav" className="pc-landing-mobile-nav" aria-label="Mobile Navigation" hidden={!menuOpen}>
          {navigation.map(([id, label]) => <a key={id} href={`#${id}`} onClick={() => setMenuOpen(false)}>{label}<ArrowUpRight size={20} aria-hidden="true" /></a>)}
        </nav>
      </header>

      <main id="pc-landing-main">
        <section className="pc-landing-hero pc-landing-shell" aria-labelledby="pc-landing-title">
          <div className="pc-landing-hero__layout">
            <div className="pc-landing-hero__copy">
              <h1 id="pc-landing-title" className="pc-landing-hero__primary">
                <span>Your game.</span>{" "}<span>Our care.</span>
              </h1>
              <p className="pc-landing-hero__secondary">Persönliche Betreuung für Profisportler.</p>
              <p className="pc-landing-hero__detail">Dein Care Manager kümmert sich um Ernährung, Recovery und alles, was neben dem Sport anfällt.</p>
              <div className="pc-landing-hero__actions"><RequestButton /><Link className="pc-landing-text-link" to="/login">Login <ArrowUpRight size={17} aria-hidden="true" /></Link></div>
            </div>
            <LandingHeroStage />
          </div>
          <a href="#leistungen" className="pc-landing-scroll" aria-label="Weiter zu den Leistungen"><ArrowDown size={19} aria-hidden="true" /> Entdecke Performance Care</a>
        </section>

        <section id="leistungen" className="pc-landing-offer pc-landing-paper" aria-labelledby="pc-landing-concept-title">
          <div className="pc-landing-shell">
            <div className="pc-landing-offer__intro">
              <div><p className="pc-landing-label">Rund um deine Leistung</p>
              <h2 id="pc-landing-concept-title">Profisport endet nicht<br /> am Trainingsplatz.</h2></div>
              <p className="pc-landing-offer__lead">Performance Care übernimmt, was neben Training und Spiel organisiert werden muss.</p>
            </div>
            <ul className="pc-landing-offer__items" aria-label="Was Performance Care übernimmt">
              {services.map(([name, terms]) => <li key={name}><h3>{name}</h3><p>{terms.map(term => <span key={term}>{term}</span>)}</p></li>)}
            </ul>
          </div>
        </section>

        <section id="betreuung" className="pc-landing-section pc-landing-care" aria-labelledby="pc-landing-care-title">
          <div className="pc-landing-shell pc-landing-care__layout">
            <h2 id="pc-landing-care-title"><span>Du sagst, was du brauchst.</span>{" "}<span className="pc-landing-muted">Wir organisieren den Rest.</span></h2>
            <LandingCareDemo />
          </div>
        </section>

        <section id="gamepower" className="pc-landing-section pc-landing-gamepower" aria-labelledby="pc-landing-gamepower-title">
          <div className="pc-landing-shell">
            <LandingGamepowerScroll intro={<div className="pc-landing-gamepower__intro"><p className="pc-landing-label">Der Spielzyklus</p><h2 id="pc-landing-gamepower-title">Gamepower.</h2><p>Gamepower zeigt dir, wie gut du für das nächste Spiel vorbereitet bist. Je mehr du umsetzt, desto besser sind deine Voraussetzungen für den Spieltag.</p></div>} />
          </div>
        </section>

        <section id="app" className="pc-landing-section pc-landing-app" aria-labelledby="pc-landing-app-title">
          <div className="pc-landing-shell pc-landing-app__layout">
            <div className="pc-landing-app__copy">
              <p className="pc-landing-label">Die Performance Care App</p>
              <h2 id="pc-landing-app-title">Dein Alltag.<br /><span className="pc-landing-muted">Klar organisiert.</span></h2>
              <div className="pc-landing-app__tabs" role="tablist" aria-label="App-Bereiche" onKeyDown={selectAppView}>{Object.entries(appViews).map(([id, view]) => <button key={id} id={`pc-landing-tab-${id}`} type="button" role="tab" aria-selected={appView === id} aria-controls="pc-landing-app-panel" tabIndex={appView === id ? 0 : -1} onClick={() => setAppView(id)}>{view.label}</button>)}</div>
              <div className="pc-landing-app__description" aria-live="polite">{Object.entries(appViews).map(([id, view]) => <div key={id} data-active={appView === id} aria-hidden={appView !== id}><h3>{view.title}</h3></div>)}</div>
            </div>
            <div id="pc-landing-app-panel" className="pc-landing-app__stage" data-media-slot="app" data-media-state="asset" role="tabpanel" aria-labelledby={`pc-landing-tab-${appView}`} tabIndex={0}>
              <div className="pc-landing-app__visual"><LandingPhonePreview view={appView} /></div>
              <p className="pc-landing-example">App-Ansichten · Synthetische Beispieldaten</p>
            </div>
          </div>
        </section>

        <section className="pc-landing-close pc-landing-shell" aria-labelledby="pc-landing-close-title">
          <p className="pc-landing-label">Performance Care</p>
          <h2 id="pc-landing-close-title"><span>Mehr Fokus.</span>{" "}<span>Weniger Organisation.</span></h2>
          <p className="pc-landing-close__promise">Du konzentrierst dich auf deine Leistung. Wir kümmern uns um den Rest.</p>
          <div className="pc-landing-close__actions">
            <RequestButton />
            <Link className="pc-landing-text-link" to="/login">Bereits Kunde? Login <ArrowUpRight size={17} aria-hidden="true" /></Link>
          </div>
        </section>
      </main>

      <footer className="pc-landing-footer pc-landing-shell"><Brand /><span>Personal athlete care.</span><div><Link to="/datenschutz">Datenschutz</Link><Link to="/login">Login</Link><a href="#pc-landing-main" aria-label="Nach oben"><ArrowUpRight size={19} aria-hidden="true" /></a></div></footer>
    </div>
  );
}
